import { forwardRef, useId, type ComponentProps } from 'react';
import { Input, Label, cn } from '@/lib/macondo';

type FieldProps = Omit<ComponentProps<'input'>, 'id' | 'size'> & {
  label: string;
  /** Error text. Rendered in a polite live region that is always in the DOM, so changes are announced. */
  error?: string | null | undefined;
  hint?: string | undefined;
  inputClassName?: string | undefined;
};

/**
 * A labelled input: visible label above the field, hint and error tied to it with aria-describedby,
 * aria-invalid when there is an error. The error container is rendered even when empty so screen
 * readers announce text inserted into it.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ label, error, hint, className, inputClassName, ...rest }, ref) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, errorId].filter(Boolean).join(' ');
  return (
    <div className={cn('flex flex-col gap-100', className)}>
      <Label htmlFor={id} className="text-callout-medium text-typography-primary">
        {label}
      </Label>
      <Input
        ref={ref}
        id={id}
        size="lg"
        invalid={!!error}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn('min-h-[48px] w-full', inputClassName)}
        {...rest}
      />
      {hint ? (
        <p id={hintId} className="text-footnote text-typography-secondary">
          {hint}
        </p>
      ) : null}
      <p id={errorId} aria-live="polite" className={cn('text-footnote text-critical-text', !error && 'sr-only')}>
        {error ?? ''}
      </p>
    </div>
  );
});
